import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api/auth';

const AuthContext = createContext(null);

/** Возвращает активное хранилище: localStorage если "запомнить", иначе sessionStorage */
export const getAuthStorage = () =>
  localStorage.getItem('remember_me') === '1' ? localStorage : sessionStorage;

// Дефолтная матрица доступа (fallback)
// admin не включён - имеет полный доступ по умолчанию
// Роли: finance_admin, senior_manager
const DEFAULT_PAGE_ACCESS = [
  { page: 'Dashboard', path: '/dashboard', finance_admin: true, senior_manager: true },
  { page: 'Проекты (Overview)', path: '/projects', finance_admin: true, senior_manager: true },
  { page: 'Проекты (Список)', path: '/projects/list', finance_admin: true, senior_manager: true },
  { page: 'Создание проекта', path: '/projects/new', finance_admin: true, senior_manager: true },
  { page: 'Детали проекта', path: '/projects/:id', finance_admin: true, senior_manager: true },
  { page: 'Gantt-диаграмма', path: '/projects/:id/gantt', finance_admin: true, senior_manager: true },
  { page: 'Сотрудники', path: '/employees', finance_admin: true, senior_manager: true },
  { page: 'Конструктор отчётов', path: '/report-builder', finance_admin: true, senior_manager: true },
  { page: 'Bitrix интеграция', path: '/integrations/bitrix', finance_admin: true, senior_manager: false },
  { page: 'Field Registry', path: '/admin/field-registry', finance_admin: true, senior_manager: false },
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [pageAccess, setPageAccess] = useState(DEFAULT_PAGE_ACCESS);
  const [loading, setLoading] = useState(true);

  // Загрузка матрицы доступа
  const loadPageAccess = useCallback(async () => {
    const storage = getAuthStorage();
    try {
      const data = await authAPI.getPageAccess();
      if (data && data.length > 0) {
        setPageAccess(data);
        storage.setItem('page_access', JSON.stringify(data));
      }
    } catch (error) {
      console.log('Page access not loaded, using defaults');
      const stored = storage.getItem('page_access');
      if (stored) {
        setPageAccess(JSON.parse(stored));
      }
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    // localStorage = "remember me", sessionStorage = session only
    const storage = getAuthStorage();
    const token = storage.getItem('access_token');
    const storedUser = storage.getItem('user');
    const storedAccess = storage.getItem('page_access');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      if (storedAccess) {
        setPageAccess(JSON.parse(storedAccess));
      }
      // Обновляем матрицу доступа с сервера
      loadPageAccess();
    }
    setLoading(false);
  }, [loadPageAccess]);

  const login = async (email, password, rememberMe = false) => {
    const data = await authAPI.login(email, password);

    // Выбираем хранилище: localStorage = помнить, sessionStorage = до закрытия
    const storage = rememberMe ? localStorage : sessionStorage;
    // Очищаем оба хранилища перед записью
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');

    storage.setItem('access_token', data.access_token);
    storage.setItem('user', JSON.stringify(data.user));
    // Флаг "запомнить" всегда в localStorage (чтобы знать где искать при перезагрузке)
    localStorage.setItem('remember_me', rememberMe ? '1' : '0');

    setUser(data.user);
    setMustChangePassword(data.must_change_password || false);

    // Загружаем матрицу доступа после логина
    await loadPageAccess();

    return data;
  };

  const logout = () => {
    // Очищаем оба хранилища
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('page_access');
    localStorage.removeItem('remember_me');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('page_access');
    setUser(null);
    setMustChangePassword(false);
    setPageAccess(DEFAULT_PAGE_ACCESS);
  };

  // Проверка доступа к странице по path
  const hasPageAccess = useCallback((path) => {
    if (!user) return false;

    const role = user.role?.toLowerCase();

    // Admin имеет полный доступ - не проверяем матрицу
    if (role === 'admin') return true;

    // Goszakup - изолированная роль, доступ ТОЛЬКО к /goszakup
    if (role === 'goszakup') {
      return path === '/goszakup' || path === '/login';
    }

    // Роли которые проверяем через матрицу
    const matrixRoles = ['finance_admin', 'senior_manager'];

    if (!matrixRoles.includes(role)) {
      // Для неизвестных ролей - доступ по умолчанию
      return true;
    }

    // Ищем правило для данного path
    // Сначала точное совпадение
    let rule = pageAccess.find(r => r.path === path);

    // Если не нашли - проверяем паттерны с :id
    if (!rule) {
      // Заменяем конкретные id на :id для матчинга
      const normalizedPath = path.replace(/\/[a-f0-9-]{36}/gi, '/:id');
      rule = pageAccess.find(r => r.path === normalizedPath);
    }

    // Если не нашли - проверяем начало пути
    if (!rule) {
      rule = pageAccess.find(r => {
        const basePath = r.path.replace('/:id', '').replace('/:id/gantt', '');
        return path.startsWith(basePath) && basePath.length > 1;
      });
    }

    if (!rule) {
      // Если правило не найдено - разрешаем (безопасный дефолт для неизвестных страниц)
      return true;
    }

    return rule[role] === true;
  }, [user, pageAccess]);

  // Обновление матрицы (вызывается после изменения в UI)
  const refreshPageAccess = useCallback(async () => {
    await loadPageAccess();
  }, [loadPageAccess]);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      mustChangePassword,
      setMustChangePassword,
      pageAccess,
      hasPageAccess,
      refreshPageAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
